/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {BoundaryMode} from '@ion/dls/src/types/types';
import useBoundaryFilter from '@ion/ion-suite/src/routes/tracking/hooks/tracking/useBoundaryFilter';
import {boundaryModeSelector, contextMenuLocationSelector} from '@/store/selectors/tracking-selectors';

/**
 * The useBoundaryPanelProps hook is used for sharing props to Boundary Panel
 * 
 * ```typescript
 * 
 *   import useBoundaryPanelProps from './hooks/useBoundaryPanelProps';
 * 
 *   const boundaryPanelProps = useBoundaryPanelProps();
 * 
 *   // boundaryPanelProps
 * 
 * ```
 */
export const useBoundaryPanelProps = () => {
  const boundaryMode        = useSelector(boundaryModeSelector);
  const contextMenuLocation = useSelector(contextMenuLocationSelector);

  const {
    boundary,
    boundaryPanelOpen,
    selectedBoundaryTool,
    onApplyBoundary,
    toggleShowBoundaryPanel
  } = useBoundaryFilter({});

  const vicinitySearchprops = useMemo(() => {
    if (boundaryMode === BoundaryMode.Vicinity) {
      return ({
        mapPoint : contextMenuLocation?.mapPoint
      });
    }
  }, [boundaryMode, contextMenuLocation]);
  
  const boundaryPanelProps = useMemo(() => ({
    boundary,
    boundaryMode,
    vicinitySearchprops,
    boundaryPanelOpen,
    selectedBoundaryTool,
    onApplyBoundary,
    toggleShowBoundaryPanel
  }), [
    boundary,
    boundaryMode,
    vicinitySearchprops,
    boundaryPanelOpen,
    selectedBoundaryTool,
    onApplyBoundary,
    toggleShowBoundaryPanel
  ]);

  return boundaryPanelProps;
};

export default useBoundaryPanelProps;
